import {
  forwardRef,
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  Ref,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { classNames } from "../utils/classNames";
import { Icon, IconProps } from "./Icon";
import { observer } from "mobx-react";
import { handleEnter } from "../utils/keyboardEvents";
import { deferred, when } from "../utils/functions";
import { isIconName } from "./iconHelpers";
import { IconButton } from "./Button";
import { mergeRefs, useElementId } from "../utils/reactHelpers";
import { FocusTooltip } from "./Tooltip";
import { isTMessage, TMessage } from "../i18n/tt";
import {
  DCChangeEventHandler,
  DCClipboardEventHandler,
  DCFocusEventHandler,
  DCKeyboardEventHandler,
  useChangeEventHandler,
  useClipboardEventHandler,
  useFocusEventHandler,
  useKeyboardEventHandler,
  useLanguage,
  useTranslation,
} from "../stores/RootStoreContext";
import { NumberFieldModel, StringFieldModel } from "../common/commonForms";
import { TableContext } from "./TableContext";

export type TextInputProps = {
  id?: string;
  disabled?: boolean;
  error?: boolean;
  icon?: ReactElement | string | number | IconProps["name"];
  addon?: ReactElement;
  addonPosition?: "left" | "right";
  prefix?: string;
  label?: string | TMessage | ReactNode;
  message?: string;
  messageVariant?: "default" | "tooltip";
  name?: string;
  onChange?: DCChangeEventHandler<HTMLInputElement>;
  onFocus?: DCFocusEventHandler<HTMLElement>;
  onBlur?: DCFocusEventHandler<HTMLElement>;
  onKeyDown?: DCKeyboardEventHandler<HTMLInputElement>;
  onPaste?: DCClipboardEventHandler<HTMLInputElement>;
  placeholder?: string | TMessage;
  value?: string | number;
  autofocus?: boolean;
  autoSelect?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  readOnly?: boolean;
  type?: "text" | "password" | "number" | "search";
  inline?: boolean;
  align?: "left" | "center" | "right";
  variant?: "in-table";
  compact?: boolean;
  testId?: string;
  autoComplete?: string;
};

export const TextInput = forwardRef<HTMLDivElement, TextInputProps>(function TextInput(
  {
    id,
    disabled,
    error,
    icon,
    addon,
    addonPosition = "right",
    prefix,
    label,
    message,
    messageVariant = "default",
    name,
    placeholder,
    value,
    autofocus,
    autoSelect,
    inputRef,
    inputProps,
    readOnly,
    type,
    inline,
    align = "left",
    variant,
    testId,
    autoComplete,
    ...props
  },
  ref,
) {
  const { t } = useTranslation();
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const inputId = useElementId(id);
  const tableContext = useContext(TableContext);
  const inTable = variant === "in-table" || tableContext?.editable;
  const compact = props.compact || tableContext?.compact;
  const internalInputRef = useRef<HTMLInputElement>(null);
  const focusHandler = useFocusEventHandler(props.onFocus);
  const blurHandler = useFocusEventHandler(props.onBlur);
  const changeHandler = useChangeEventHandler(props.onChange);
  const keyDownHandler = useKeyboardEventHandler(props.onKeyDown);
  const pasteHandler = useClipboardEventHandler(props.onPaste);

  useEffect(() => {
    if (autoSelect) {
      internalInputRef.current?.select();
    }
  }, [autoSelect]);

  const renderInput = (tooltipTargetRef?: (el: HTMLElement | null) => void) => (
    <input
      id={inputId}
      ref={mergeRefs(internalInputRef, inputRef, tooltipTargetRef)}
      disabled={disabled}
      className={classNames(
        "text-input--input",
        "tw-flex-1",
        "focus-visible:tw-outline-none",
        compact
          ? "tw-p-1"
          : [
              "tw-py-3",
              icon || prefix ? "tw-pl-2" : "tw-pl-4",
              type !== "password" && !(addon && addonPosition === "right") && "tw-pr-4",
            ],
        "tw-w-full",
        "bg-transparent",
        "disabled:bg-transparent",
        align === "center"
          ? "tw-text-center"
          : align === "right"
            ? "tw-text-right"
            : "tw-text-left",
      )}
      type={passwordVisible ? "text" : (type ?? "text")}
      name={name}
      placeholder={isTMessage(placeholder) ? t(placeholder) : placeholder}
      value={value}
      onChange={changeHandler}
      onFocus={focusHandler}
      onBlur={blurHandler}
      onKeyDown={keyDownHandler}
      onPaste={pasteHandler}
      autoFocus={autofocus}
      readOnly={readOnly}
      data-testid={testId}
      autoComplete={autoComplete}
      {...inputProps}
    />
  );

  return (
    <div className={classNames(!inline && "tw-grow")}>
      {label && (
        <label
          htmlFor={inputId}
          className={classNames("tw-block tw-mb-1", inTable && "tw-sr-only")}
        >
          {isTMessage(label) ? t(label) : label}
        </label>
      )}
      <div
        ref={ref}
        className={classNames(
          "tw-relative",
          inline ? "tw-inline-flex tw-w-60" : "tw-flex tw-w-full tw-min-w-[10ch]",
          "tw-grow",
          inTable && "tw-w-full",
          "tw-items-center",
          "tw-leading-tight",
          !inTable && "tw-border tw-rounded",
          "has-[.text-input--input:focus]:tw-ring-1",
          "tw-cursor-text",
          error
            ? [
                "color-default",
                "bg-default",
                "border-error",
                "focus-within:border-error",
                "focus-within:ring-error",
                "hover:border-error",
                "hover:focus-within:border-error",
                inTable && [
                  "before:tw-left-0",
                  "before:tw-right-0",
                  "before:tw-bottom-0",
                  "before:tw-absolute",
                  "before:border-error",
                  "before:tw-border-b-2",
                  "before:focus-within:tw-border-none",
                ],
              ]
            : disabled
              ? ["subtle", "bg-gray-x-soft", "border-gray-soft", "hover:border-gray-soft"]
              : [
                  "color-default",
                  "bg-default",
                  "border-gray",
                  "has-[.text-input--input:focus]:border-primary",
                  "has-[.text-input--input:focus]:ring-primary",
                  "hover:border-gray-hard",
                ],
        )}
      >
        {addonPosition === "left" && addon}
        {icon && (
          <div
            className={classNames(
              "tw-text-xl",
              "tw-leading-5",
              "tw-h-5",
              "subtle",
              compact ? "tw-ml-1" : "tw-ml-4",
              disabled && "tw-pointer-events-none",
            )}
          >
            {isIconName(icon) ? <Icon name={icon} /> : icon}
          </div>
        )}
        {prefix && (
          <div
            className={classNames(
              "tw-flex-none",
              compact ? "tw-ml-1" : icon ? "tw-ml-2" : "tw-ml-4",
            )}
          >
            {prefix}
          </div>
        )}
        {inTable || messageVariant === "tooltip" ? (
          <FocusTooltip content={message} color={error ? "error" : undefined} placement={"top"}>
            {({ targetRef }) => renderInput(targetRef)}
          </FocusTooltip>
        ) : (
          renderInput()
        )}
        {addonPosition !== "left" && type === "password" && (
          <IconButton
            icon={passwordVisible ? "eye-closed" : "eye"}
            tooltip={passwordVisible ? t("hide-password") : t("show-password")}
            onClick={() => setPasswordVisible(!passwordVisible)}
            compact={compact}
          />
        )}
        {addonPosition === "right" && addon}
      </div>
      {message && messageVariant === "default" && !inTable && (
        <div
          className={classNames("tw-pt-1", "tw-pl-4", "text-sm", error ? "color-error" : "subtle")}
        >
          {message}
        </div>
      )}
    </div>
  );
});

type MTextInputProps = {
  model: StringFieldModel | NumberFieldModel;
} & Omit<TextInputProps, "value">;

export const MTextInput = observer(function MTextInput({
  model,
  onChange,
  onBlur,
  onPaste,
  onKeyDown,
  ...props
}: MTextInputProps) {
  const { t } = useTranslation();
  const locale = useLanguage();

  useEffect(() => {
    if ("setLocale" in model) {
      model.setLocale(locale);
    }
  }, [locale, model]);

  return (
    <TextInput
      {...props}
      inputRef={model.setRef}
      value={model.inputValue}
      onChange={onChange ?? model.setInputValue}
      onBlur={onBlur ?? model.commit}
      onKeyDown={onKeyDown ?? handleEnter(model.commit)}
      onPaste={onPaste ?? deferred(model.commit)}
      error={props.error ?? !model.isValid}
      message={props.message ?? when(model.error, t)}
    />
  );
});
